import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Loader2, MessageCircle, RefreshCw, Send, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export const ThumbtackMessages = ({ job }) => {
  const negId = job?.thumbtack_negotiation_id;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [meta, setMeta] = useState({});
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [sendError, setSendError] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async (soft = false) => {
    if (!negId) return;
    soft ? setRefreshing(true) : setLoading(true);
    try {
      const res = await axios.get(`${API}/thumbtack/negotiations/${negId}/messages`, { headers: headers() });
      setMessages(res.data.messages || []);
      setMeta({ stale: res.data.stale, error: res.data.error, writeEnabled: res.data.messaging_write_enabled });
    } catch (e) {
      const d = e.response?.data?.detail;
      setMeta({ stale: true, error: (typeof d === "object" ? d?.code : null) || "load_failed" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [negId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const res = await axios.post(`${API}/thumbtack/negotiations/${negId}/messages`, { text }, { headers: headers() });
      setMessages((prev) => [...prev, res.data.message]);
      setDraft(""); // cleared only on success — a failed reply stays for retry
    } catch (e) {
      const d = e.response?.data?.detail;
      setSendError(typeof d === "object" && d ? d : { code: "send_failed", message: typeof d === "string" ? d : "Could not send the message. Try again." });
    } finally {
      setSending(false);
    }
  };

  const suggest = async () => {
    if (suggesting || sending) return;
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/thumbtack/negotiations/${negId}/suggest-reply`, {}, { headers: headers() });
      setDraft(res.data.suggestion || "");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not draft a suggestion — try again.");
    } finally {
      setSuggesting(false);
    }
  };

  if (!negId) return null;
  const needsReconnect = sendError?.code === "missing_scope" || sendError?.code === "reconnect_required" || meta.error === "reconnect_required";

  return (
    <div className="rounded-xl border border-[#009fd9]/25 bg-[#009fd9]/[0.04] p-3 space-y-2" data-testid="thumbtack-messages">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <MessageCircle className="w-3 h-3 text-[#009fd9]" /> Thumbtack Messages
        </p>
        <button
          type="button"
          onClick={() => load(true)}
          className="p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted"
          title="Refresh thread"
          data-testid="thumbtack-thread-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {messages.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2" data-testid="thumbtack-thread-empty">
              No messages on this lead yet.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1" data-testid="thumbtack-thread">
              {messages.map((m) => (
                <div key={m.message_id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-1.5 ${m.direction === "outbound" ? "bg-[#009fd9]/15 text-foreground" : "bg-muted text-foreground"}`}
                    data-testid={`thumbtack-msg-${m.message_id}`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {m.direction === "outbound" ? "You" : m.sender_name || "Customer"}{fmtTime(m.sent_at) ? ` · ${fmtTime(m.sent_at)}` : ""}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}

          {meta.error === "reconnect_required" && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 flex items-center gap-2" data-testid="thumbtack-reconnect-prompt">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <p className="text-[11px] text-foreground">
                Thumbtack needs reconnection — reconnect it in <span className="font-medium">Settings → Integrations</span> to refresh and reply.
              </p>
            </div>
          )}
          {meta.stale && meta.error && meta.error !== "reconnect_required" && messages.length > 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="thumbtack-thread-stale">
              Showing saved copy — couldn't refresh from Thumbtack right now.
            </p>
          )}

          {sendError && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2 space-y-1.5" data-testid="thumbtack-msg-error">
              <p className="text-[11px] text-red-500">{sendError.message || "Could not send the message."}</p>
              {needsReconnect ? (
                <a
                  href="/dashboard/settings?tab=integrations"
                  className="inline-block text-[11px] font-medium underline text-foreground"
                  data-testid="thumbtack-msg-reconnect-link"
                >
                  Reconnect Thumbtack
                </a>
              ) : (
                <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={send} disabled={sending} data-testid="thumbtack-msg-retry">
                  Retry
                </Button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2.5 border-[#009fd9]/40 text-[#009fd9] hover:bg-[#009fd9]/10 shrink-0"
              onClick={suggest}
              disabled={suggesting || sending || messages.length === 0}
              title="AI: draft a suggested reply"
              data-testid="thumbtack-suggest-btn"
            >
              {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            </Button>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Reply to this Thumbtack lead…"
              className="flex-1 min-w-0 h-8 px-2.5 rounded-lg bg-background border border-border/60 text-sm text-foreground outline-none focus:border-[#009fd9]/60"
              data-testid="thumbtack-reply-input"
            />
            <Button
              size="sm"
              className="h-8 px-3 bg-[#009fd9] text-white hover:bg-[#008ec2]"
              onClick={send}
              disabled={sending || !draft.trim()}
              data-testid="thumbtack-send-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ThumbtackMessages;
